// PKCE helpers for the Cognito Hosted UI authorization-code flow.
//
// The flow:
//   1. Generate a code_verifier (random 32+ bytes, base64url-encoded).
//   2. SHA-256 the verifier → base64url-encode → that's the code_challenge.
//   3. Stash the verifier in sessionStorage so the callback page can pick
//      it up after Cognito redirects back.
//   4. Send the user to Cognito's /oauth2/authorize with the challenge.
//   5. On callback, POST to /oauth2/token with the original verifier; get
//      id_token + access_token + refresh_token back.
//
// All client-side. No secret. PKCE is what makes this safe to ship as a
// public SPA. Spec: RFC 7636.

const VERIFIER_BYTES = 32;
const STATE_BYTES = 16;
const SESSION_KEY_VERIFIER = 'tee-admin-pkce-verifier';
const SESSION_KEY_STATE = 'tee-admin-pkce-state';
const SESSION_KEY_RETURN_TO = 'tee-admin-pkce-return-to';

function base64UrlEncode(bytes: Uint8Array): string {
  // btoa wants a binary string. Build it byte-by-byte; chunking would only
  // matter for very large inputs (we're hashing 32 bytes).
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

async function sha256(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

export interface PkceChallenge {
  verifier: string;
  challenge: string;
  state: string;
}

/** Generate a fresh verifier + challenge + opaque state token. The caller
 *  is responsible for stashing the verifier and state via stashPkce(). */
export async function generatePkce(): Promise<PkceChallenge> {
  const verifier = base64UrlEncode(randomBytes(VERIFIER_BYTES));
  const challenge = base64UrlEncode(await sha256(verifier));
  const state = base64UrlEncode(randomBytes(STATE_BYTES));
  return { verifier, challenge, state };
}

/** Persist the verifier + state in sessionStorage so the OAuth callback page
 *  can retrieve them. sessionStorage (not localStorage) so closing the tab
 *  cleans up any half-finished flow. */
export function stashPkce(c: PkceChallenge, returnTo: string): void {
  sessionStorage.setItem(SESSION_KEY_VERIFIER, c.verifier);
  sessionStorage.setItem(SESSION_KEY_STATE, c.state);
  sessionStorage.setItem(SESSION_KEY_RETURN_TO, returnTo);
}

export interface ConsumedPkce {
  verifier: string;
  state: string;
  returnTo: string;
}

/** Read + clear the stashed PKCE state. Returns null if any piece is
 *  missing (the user opened the callback URL directly, or sessionStorage
 *  got wiped). */
export function consumePkce(): ConsumedPkce | null {
  const verifier = sessionStorage.getItem(SESSION_KEY_VERIFIER);
  const state = sessionStorage.getItem(SESSION_KEY_STATE);
  const returnTo = sessionStorage.getItem(SESSION_KEY_RETURN_TO) ?? '/';
  sessionStorage.removeItem(SESSION_KEY_VERIFIER);
  sessionStorage.removeItem(SESSION_KEY_STATE);
  sessionStorage.removeItem(SESSION_KEY_RETURN_TO);
  if (!verifier || !state) return null;
  return { verifier, state, returnTo };
}
